import React, { useState, useEffect, useCallback } from 'react';
import { 
  Box, 
  TextField, 
  IconButton, 
  CircularProgress, 
  Alert,
  InputAdornment
} from '@mui/material';
import { Search, Clear, Close } from '@mui/icons-material';
import { apiCall } from '../utils/api';

const SearchBar = ({ onSearchResults, onClearSearch, familyId }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState('');

  const performSearch = useCallback(async (query) => {
    setIsSearching(true);
    setSearchError('');
    
    try {
      const results = await apiCall(`/api/families/${familyId}/runbooks/search?q=${encodeURIComponent(query)}`);
      
      // Check if results is an error object
      if (results && results.error) {
        throw new Error(results.error);
      }
      
      // Ensure results is an array
      const safeResults = Array.isArray(results) ? results : [];
      onSearchResults(safeResults, query);
      
    } catch (error) {
      console.error('Search error:', error);
      setSearchError('Search failed. Please try again.');
      onSearchResults([], query);
    } finally {
      setIsSearching(false);
    }
  }, [familyId, onSearchResults]);

  const handleClear = useCallback(() => {
    setSearchQuery('');
    setSearchError('');
    onClearSearch();
  }, [onClearSearch]);

  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      if (searchQuery.trim()) {
        performSearch(searchQuery.trim());
      } else {
        onClearSearch();
        setSearchError('');
      }
    }, 300); // Debounce search by 300ms

    return () => clearTimeout(delayedSearch);
  }, [searchQuery, performSearch, onClearSearch]);

  return (
    <Box sx={{ width: '100%', mb: 2 }}>
      <TextField
        fullWidth
        placeholder="Search runbooks by title, category, or description..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        variant="outlined"
        size="medium"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search color="action" />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              {isSearching && (
                <CircularProgress size={20} />
              )}
              {searchQuery && !isSearching && (
                <IconButton
                  onClick={handleClear}
                  size="small"
                  title="Clear search"
                  edge="end"
                >
                  <Clear />
                </IconButton>
              )}
            </InputAdornment>
          ),
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: 2,
          },
        }}
      />
      
      {searchError && (
        <Alert 
          severity="error" 
          sx={{ mt: 1 }}
          action={
            <IconButton
              color="inherit"
              size="small"
              onClick={() => setSearchError('')}
            >
              <Close fontSize="inherit" />
            </IconButton>
          }
        >
          {searchError}
        </Alert>
      )}
    </Box>
  );
};

export default SearchBar;